
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

export function useKitMarket(kitId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: kitMarket } = useQuery({
    queryKey: ['kit-market', kitId],
    queryFn: async () => {
      if (!kitId) return null;
      const { data, error } = await supabase
        .from('kit_markets')
        .select('*')
        .eq('kit_id', kitId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!kitId
  });

  const updateKitMarketMutation = useMutation({
    mutationFn: async (market_short_name: string) => {
      if (!kitId) throw new Error('No kit ID provided');
      
      const { data, error } = await supabase
        .from('kit_markets')
        .upsert(
          {
            kit_id: kitId,
            market_short_name
          },
          {
            onConflict: 'kit_id',
            ignoreDuplicates: false
          }
        )
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kit-market', kitId] });
      toast({
        title: "Market updated",
        description: "The market selection has been saved.",
        className: "bg-[#1A1F2C] border border-white/10 text-white",
      });
    },
    onError: (error) => {
      console.error('Error updating market:', error);
      toast({
        title: "Error",
        description: "Failed to update market. Please try again.",
        variant: "destructive",
        className: "bg-red-950 border border-red-900 text-white",
      });
    },
  });

  return {
    kitMarket,
    updateKitMarketMutation
  };
}
